import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import CrearCampana from '../components/CrearCampana';

const ESTADO_STYLES = {
  borrador:  'bg-gray-100 text-gray-700',
  activa:    'bg-green-100 text-green-700',
  pausada:   'bg-yellow-100 text-yellow-700',
  finalizada:'bg-red-100 text-red-700',
  expirada:  'bg-orange-100 text-orange-700',
  archivada: 'bg-gray-200 text-gray-500',
};

const ESTADO_LABEL = {
  borrador:  'Borrador',
  activa:    'Activa',
  pausada:   'Pausada',
  finalizada:'Finalizada',
  expirada:  'Expirada',
  archivada: 'Archivada',
};

const Campanas = () => {
  const [campanas, setCampanas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [modalCrear, setModalCrear] = useState(false);
  const navigate = useNavigate();

  const fetchCampanas = async (incluirArchivadas = false) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const params = incluirArchivadas ? '?incluir_archivadas=true' : '';
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/campanas/${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCampanas(res.data);
    } catch {
      setError('Error al cargar campañas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampanas(filtroEstado === 'archivada');
  }, [filtroEstado]);

  const filtradas = filtroEstado
    ? campanas.filter((c) => c.estado === filtroEstado)
    : campanas;

  return (
    <div className="h-full flex flex-col p-2 md:p-4 overflow-hidden">
      <div className="flex-1 min-h-0 flex flex-col bg-white rounded-3xl p-2 md:p-4 overflow-hidden">

        {/* Cabecera Marketing */}
        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
          <p className="text-xl md:text-3xl font-extrabold tracking-tight text-slate-900">Marketing</p>
        </div>

        {/* Sección Campañas */}
        <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
          <p className="text-base font-semibold text-slate-600">Campañas</p>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none"
            >
              <option value="">Activas y vigentes</option>
              <option value="borrador">Borrador</option>
              <option value="activa">Activa</option>
              <option value="pausada">Pausada</option>
              <option value="expirada">Expirada</option>
              <option value="finalizada">Finalizada</option>
              <option value="archivada">Archivadas</option>
            </select>
            <button
              onClick={() => setModalCrear(true)}
              className="px-4 py-2 rounded-lg text-white text-sm font-semibold"
              style={{ backgroundColor: '#FF743A' }}
            >
              + Nueva campaña
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center flex-1 gap-3 text-gray-400">
            <div className="w-8 h-8 border-4 border-gray-200 border-t-orange-400 rounded-full animate-spin" />
            <p className="text-sm">Cargando campañas...</p>
          </div>
        )}
        {error && <p className="text-center py-12 text-red-500">{error}</p>}

        {!loading && !error && (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-3 font-semibold text-gray-600 rounded-tl-lg">Nombre</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Código</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Descuento</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Estado</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Enviados</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Canjeados</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 rounded-tr-lg">Conversión</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-gray-400">
                      No hay campañas{filtroEstado ? ` en estado "${ESTADO_LABEL[filtroEstado] || filtroEstado}"` : ''}.
                    </td>
                  </tr>
                ) : (
                  filtradas.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => navigate(`/marketing/${c.id}`)}
                      className="border-t border-gray-100 hover:bg-orange-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">{c.nombre}</td>
                      <td className="px-4 py-3 font-mono text-gray-600">{c.codigo}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {c.tipo_descuento === 'porcentaje'
                          ? `${c.valor_descuento}%`
                          : `$${Number(c.valor_descuento).toLocaleString('es-CL')}`}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${ESTADO_STYLES[c.estado] || 'bg-gray-100 text-gray-600'}`}>
                          {ESTADO_LABEL[c.estado] || c.estado}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{c.total_enviados ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-700">{c.total_canjeados ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {c.conversion_pct != null ? `${Number(c.conversion_pct).toFixed(1)}%` : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalCrear && (
        <CrearCampana
          onClose={() => setModalCrear(false)}
          onSuccess={() => { setModalCrear(false); fetchCampanas(false); }}
        />
      )}
    </div>
  );
};

export default Campanas;